import { computed, ref, watch } from 'vue';
import axios from 'axios';

/**
 * Campo de texto libre de la ficha de una cuenta cliente que se guarda por
 * separado contra `/client-account/update/{id}`: las observaciones de cuenta
 * (ADS-0152) y los comentarios internos de ADS (ADS-0181).
 *
 * @param {object} options
 * @param {string|number} options.accountId id de la cuenta
 * @param {import('vue').Ref<object|null>} options.account cuenta cargada; el campo se lee y escribe en ella
 * @param {string} options.field campo de la API (`observations`, `comments`)
 * @param {number} options.maxLength límite que valida la API
 * @param {string} options.messages prefijo de las traducciones (`admin.observations`)
 * @param {import('vue').Ref<boolean>} options.canEdit si quien mira puede guardarlo
 * @param {Function} options.t traductor de vue-i18n
 */
export function useAccountTextField({ accountId, account, field, maxLength, messages, canEdit, t }) {
  const saved = ref('');
  const saving = ref(false);
  const error = ref('');
  const justSaved = ref(false);

  const value = computed({
    get: () => account.value?.[field] ?? '',
    set: (val) => {
      if (account.value) account.value[field] = val;
    },
  });

  const changed = computed(() => value.value !== saved.value);

  const status = computed(() => {
    if (error.value) return error.value;
    return justSaved.value ? t(`${messages}.saved`) : '';
  });

  watch(value, () => {
    error.value = '';
    justSaved.value = false;
  });

  /** Toma como guardado lo que trae la cuenta recién cargada. */
  const reset = (loaded) => {
    saved.value = loaded?.[field] ?? '';
    error.value = '';
    justSaved.value = false;
  };

  const save = async () => {
    if (!canEdit.value || saving.value || !changed.value) return;

    const valueToSave = value.value;
    if (valueToSave.length > maxLength) {
      error.value = t(`${messages}.maxLength`);
      return;
    }

    saving.value = true;
    error.value = '';
    justSaved.value = false;

    try {
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/client-account/update/${accountId}`,
        { [field]: valueToSave || null },
        {
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      saved.value = response.data?.value?.[field] ?? valueToSave;
      // Si se siguió escribiendo mientras se guardaba, se conserva ese texto y
      // la ficha continúa marcada como pendiente.
      if (value.value === valueToSave) {
        value.value = saved.value;
        justSaved.value = true;
      }
    } catch (err) {
      console.error(`Error saving client ${field}:`, err);
      error.value = err.response?.status === 422
        ? t(`${messages}.maxLength`)
        : t(`${messages}.saveError`);
    } finally {
      saving.value = false;
    }
  };

  return { value, saving, error, changed, status, reset, save };
}
